'use client';

import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PII_TYPE_LABEL, type PiiDecision, type PiiItem } from '@/lib/pii/server-pii';
import { bidiIsolated } from '@/lib/utils/rtl';

export function PiiWarningDialog({
  items,
  onDecide,
}: {
  items: PiiItem[];
  onDecide: (choice: PiiDecision | 'edit') => void;
}) {
  const t = useTranslations('sensitiveData');

  function closeAsEdit(open: boolean) {
    if (!open) {
      onDecide('edit');
    }
  }

  return (
    <Dialog open onOpenChange={closeAsEdit}>
      <DialogContent role="alertdialog" showCloseButton={false}>
        <div className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('confirmTitle')}</DialogTitle>
            <DialogDescription asChild>
              <ul className="flex flex-col gap-1 text-sm">
                {items.map((item, index) => (
                  <li key={index} className="first-letter:uppercase">
                    {t('confirmItem', {
                      type: t(`types.${PII_TYPE_LABEL[item.type]}`),
                      // The server shows only the value's last characters; the dots carry nothing.
                      ending: bidiIsolated(item.masked.replace(/^•+/, '')),
                    })}
                  </li>
                ))}
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:flex-wrap">
            <Button variant="ghost" onClick={() => onDecide('not_personal_data')}>
              {t('confirmNotPersonal')}
            </Button>
            <Button variant="outline" onClick={() => onDecide('send_anyway')}>
              {t('confirmSendAnyway')}
            </Button>
            <Button variant="accent" onClick={() => onDecide('edit')} autoFocus>
              {t('confirmEdit')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
